import { FilterDefinition, Operators } from "@monolith-forensics/monolith-ui";
import { useMemo } from "react";
import { FilesAPI } from "../files.api";
import { File } from "../files.types";
import { useCaseFiles } from "./useCaseFiles";

export const useCaseFileFilterDefinitions = () => {
  const { case_uuid } = useCaseFiles();

  const baseFilters: FilterDefinition[] = useMemo(() => {
    const filters: FilterDefinition[] = [
      {
        label: "UUID",
        pluralLabel: "UUIDs",
        dataField: "uuid",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
        ],
      },
      {
        label: "Name",
        pluralLabel: "Names",
        dataField: "name",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
        ],
      },
      {
        label: "Type",
        pluralLabel: "Types",
        dataField: "is_folder",
        selectOptions: [
          { label: "File", value: "0" },
          { label: "Folder", value: "1" },
        ],
        inputType: "multiselect",
        operators: [Operators.In, Operators.NIn],
      },
      {
        label: "Status",
        pluralLabel: "Statuses",
        dataField: "status",
        selectOptions: [
          { label: "Active", value: "active" },
          { label: "Archived", value: "archived" },
          { label: "Uploading", value: "uploading" },
          { label: "Failed", value: "failed" },
        ],
        inputType: "multiselect",
        operators: [Operators.In, Operators.NIn],
      },
      {
        label: "Size",
        pluralLabel: "Sizes",
        dataField: "size",
        inputType: "number",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.GreaterThan,
          Operators.LessThan,
          Operators.GreaterThanOrEqual,
          Operators.LessThanOrEqual,
          Operators.Between,
          Operators.NotBetween,
        ],
      },
      {
        label: "Extension",
        pluralLabel: "Extensions",
        dataField: "ext",
        inputType: "multiselect",
        operators: [Operators.In, Operators.NIn],
        query: {
          initialPageParam: 1,
          cacheKey: ({ search }) =>
            JSON.stringify(["files", "extensions", { case_uuid }]),
          loadPage: async () => {
            const page = (await FilesAPI.getFileExtensions({
              case_uuid,
            })) as unknown as {
              data: File["ext"][];
            };

            return {
              items:
                page.data?.map((extension) => ({
                  label: extension || "",
                  value: extension || "",
                  data: extension,
                })) || [],
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Path",
        pluralLabel: "Paths",
        dataField: "path",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
        ],
      },
      {
        label: "Created On",
        dataField: "created_on",
        inputType: "datetime",
        resolution: "millisecond",
        showTimeZone: "local",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.GreaterThan,
          Operators.LessThan,
          Operators.GreaterThanOrEqual,
          Operators.LessThanOrEqual,
          Operators.Between,
          Operators.NotBetween,
        ],
      },
      {
        label: "Created By",
        pluralLabel: "Created By",
        dataField: "created_by_id",
        inputType: "multiselect",
        operators: [Operators.In, Operators.NIn],
        query: {
          initialPageParam: 1,
          cacheKey: ({ search }) =>
            JSON.stringify(["files", "users", { case_uuid }]),
          loadPage: async () => {
            const page = (await FilesAPI.getUsers({
              case_uuid,
            })) as unknown as {
              data: File["created_by"][];
            };

            return {
              items:
                page.data?.map((d) => ({
                  label: d?.full_name || "",
                  value: d?.user_id?.toString() || "",
                  data: d,
                })) || [],
              nextPageParam: null,
            };
          },
        },
      },
      {
        label: "Client Modified",
        dataField: "client_modified",
        inputType: "datetime",
        resolution: "millisecond",
        showTimeZone: "local",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.GreaterThan,
          Operators.LessThan,
          Operators.GreaterThanOrEqual,
          Operators.LessThanOrEqual,
          Operators.Between,
          Operators.NotBetween,
        ],
      },
      {
        label: "MD5",
        dataField: "md5",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
        ],
      },
      {
        label: "SHA1",
        dataField: "sha1",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
        ],
      },
      {
        label: "SHA256",
        dataField: "sha256",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
        ],
      },
      {
        label: "Description",
        pluralLabel: "Descriptions",
        dataField: "description",
        inputType: "text",
        operators: [Operators.Contains, Operators.DoesNotContain],
      },
    ];

    // The Extension and Created By facets list the case's values; with no
    // case (asset attachments, ENG-2291) there is nothing to load them from.
    return case_uuid
      ? filters
      : filters.filter(
          (f) => f.dataField !== "ext" && f.dataField !== "created_by_id"
        );
  }, [case_uuid]);

  const filters = baseFilters;

  return filters;
};
